import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { usePageTitle } from '../hooks/usePageTitle';
import {
  COMPLAINT_MAX,
  JUDGE_KEY,
  JUDGE_NAME,
  OPINION_OPENERS,
  SENTENCING_GUIDELINES,
  THE_BENCH,
  computeStandings,
  fetchCases,
  fetchPhoneOptIn,
  fetchRulings,
  formatDollars,
  formatPhone,
  insertCase,
  insertPhoneOptIn,
  kourtRecord,
  kourtStatus,
  mergeByKey,
  mojitoFund,
  normalizePhone,
  pendingDocket,
  rapSheet,
  ruleOnCase,
  ruleSuaSponte,
  toCase,
  toRuling,
  verifyJudge,
  type CaseKind,
  type KourtCase,
  type KourtRuling,
  type PhoneOptIn,
  type RecordEntry,
  type Verdict,
} from '../team/kourt';
import { compressPhoto, photoUrl, uploadPhoto } from '../team/photos';
import { useTennisFeed } from '../team/roster';
import {
  USER_KEY,
  loadRuntimeSettings,
  lookupUser,
  messageRowKey,
  type PhotosConfig,
  type TablesConfig,
  type TeamUser,
} from '../team/storage';
import './Kourt.css';

const POLL_MS = 30_000;

/** Posted on the Bench. Rule 3 is enforced by the sentencing guidelines. */
const RULES = [
  'All fines are final.',
  'The Kourt hears no appeals. It would not hear yours if it did.',
  'Requesting a Venmo refund from the Kourt is itself a finable offense.',
  'Anyone may file a complaint. Only the Bench rules.',
  'The Bench may fine sua sponte — for any reason, or none.',
  'Clerical errors are corrected only by further order of the Kourt.',
  'Balances are settled in mojitos at the next home match.',
];

const VERDICT_STAMP: Record<Verdict, string> = {
  fined: 'Fined',
  bonus: 'Credited',
  dismissed: 'Dismissed',
};

interface RulingDraft {
  verdict: Verdict;
  amount: number;
  opinion: string;
}

interface SuaSponteDraft {
  defendant: string;
  verdict: 'fined' | 'bonus';
  amount: number;
  offense: string;
  opinion: string;
}

const EMPTY_SUA_SPONTE: SuaSponteDraft = { defendant: '', verdict: 'fined', amount: 5, offense: '', opinion: '' };

function whenLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function plural(n: number, word: string, pluralWord = `${word}s`): string {
  return `${n} ${n === 1 ? word : pluralWord}`;
}

/** The photo attached to a complaint, when there is one. Opens full size. */
function CasePhoto({ photos, filed }: { photos: PhotosConfig | null; filed: KourtCase }) {
  if (!filed.photoName || !photos) return null;
  const src = photoUrl(photos, filed.photoName);
  return (
    <a className="kourt-photo-link" href={src} target="_blank" rel="noreferrer">
      <span className="kourt-exhibit">Exhibit A</span>
      <img className="kourt-photo" src={src} alt={`Exhibit A, filed by ${filed.filedBy}`} loading="lazy" />
    </a>
  );
}

/** One ruling with the complaint it answered — used by the record and rap sheets. */
function RecordItem({
  entry,
  showDefendant,
  photos,
}: {
  entry: RecordEntry;
  showDefendant: boolean;
  photos: PhotosConfig | null;
}) {
  const { ruling, filed } = entry;
  const suaSponte = filed?.filedBy === THE_BENCH;
  return (
    <li className={`kourt-record kourt-record-${ruling.verdict}`}>
      <div className="kourt-record-head">
        <span className="kourt-stamp">
          {VERDICT_STAMP[ruling.verdict]}
          {ruling.verdict !== 'dismissed' && <> {formatDollars(ruling.amount)}</>}
        </span>
        {showDefendant && <span className="kourt-record-name">{ruling.defendant}</span>}
        {suaSponte && <span className="kourt-badge">sua sponte</span>}
        <span className="kourt-when">{whenLabel(ruling.ruledAt)}</span>
      </div>
      {filed && (
        <p className="kourt-charge">
          <strong>Charge:</strong> {filed.offense}
          <small>
            {' '}
            &mdash; filed by {filed.filedBy}
            {filed.filedBy !== THE_BENCH && <>, recommending {formatDollars(filed.amount)}</>}
          </small>
        </p>
      )}
      {filed && <CasePhoto photos={photos} filed={filed} />}
      <blockquote className="kourt-opinion">{ruling.opinion}</blockquote>
      {ruling.summonsName && photos && (
        <a
          className="kourt-summons"
          href={photoUrl(photos, ruling.summonsName)}
          target="_blank"
          rel="noreferrer"
        >
          📜 Summons to Appear (PDF)
        </a>
      )}
    </li>
  );
}

export default function Kourt() {
  usePageTitle("Kangaroo Kourt — Chuck's Mojito Shack");
  const feed = useTennisFeed();

  const [tables, setTables] = useState<TablesConfig | null>(null);
  const [photos, setPhotos] = useState<PhotosConfig | null>(null);
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<TeamUser | null>(null);
  const [cases, setCases] = useState<KourtCase[]>([]);
  const [rulings, setRulings] = useState<KourtRuling[]>([]);
  /** Whether the server has a passcode and storage — i.e. a gavel exists. */
  const [configured, setConfigured] = useState(false);
  const [judge, setJudge] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(JUDGE_KEY);
    } catch {
      return null;
    }
  });
  const [notice, setNotice] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const socketRef = useRef<WebSocket | null>(null);

  // File a Complaint
  const [defendant, setDefendant] = useState('');
  const [kind, setKind] = useState<CaseKind>('fine');
  const [amount, setAmount] = useState(5);
  const [offense, setOffense] = useState('');
  const [filing, setFiling] = useState(false);
  const [photoDraft, setPhotoDraft] = useState<{ blob: Blob; preview: string } | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  /** A touch screen gets a "take a photo" button that opens the camera directly. */
  const [canCapture] = useState(() => {
    try {
      return window.matchMedia('(pointer: coarse)').matches;
    } catch {
      return false;
    }
  });

  // Judge's Chambers
  const [passcode, setPasscode] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, RulingDraft>>({});
  const [rulingOn, setRulingOn] = useState<string | null>(null);
  const [suaSponte, setSuaSponte] = useState<SuaSponteDraft>(EMPTY_SUA_SPONTE);
  const [suaSponteBusy, setSuaSponteBusy] = useState(false);

  // Serve me by text
  const [optIn, setOptIn] = useState<PhoneOptIn | null>(null);
  const [optInName, setOptInName] = useState('');
  const [optInPhone, setOptInPhone] = useState('');
  const [optInBusy, setOptInBusy] = useState(false);

  /* Full fetch-and-replace: ruling keys are their case's (older) timestamp,
     so delta polling would miss them, and a replace also evicts anything a
     mischievous browser pushed over the relay that never reached storage. */
  const refresh = useCallback(async (t: TablesConfig) => {
    const [c, r] = await Promise.all([fetchCases(t), fetchRulings(t)]);
    if (c) setCases(c);
    if (r) setRulings(r);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const gavel = kourtStatus();
      const settings = await loadRuntimeSettings();
      const t = settings.tables;
      if (!cancelled) setPhotos(settings.photos);
      if (t) {
        let email = '';
        try {
          email = localStorage.getItem(USER_KEY) ?? '';
        } catch {
          /* private mode */
        }
        const [u] = await Promise.all([email ? lookupUser(t, email) : Promise.resolve(null), refresh(t)]);
        if (cancelled) return;
        setTables(t);
        setUser(u);
        if (u) {
          const current = await fetchPhoneOptIn(t, u.email);
          if (cancelled) return;
          setOptIn(current);
          if (current) setOptInName(current.rosterName);
        }
      }
      const hasGavel = await gavel;
      if (cancelled) return;
      setConfigured(hasGavel);
      setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  useEffect(() => {
    if (!tables) return;
    const id = window.setInterval(() => void refresh(tables), POLL_MS);
    return () => window.clearInterval(id);
  }, [tables, refresh]);

  /* Same relay the scoreboard uses. Complaints are pushed by the filer's
     browser; rulings are pushed by the server after it writes them. */
  useEffect(() => {
    if (!tables) return;
    let retry: number | null = null;
    let closed = false;

    const connect = () => {
      let socket: WebSocket;
      try {
        const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
        socket = new WebSocket(`${scheme}://${window.location.host}/ws/scores`);
      } catch {
        return;
      }
      socketRef.current = socket;
      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message?.type === 'case') {
            const incoming = toCase(message.data);
            if (incoming) setCases((current) => mergeByKey(current, [incoming], (c) => c.rowKey));
          } else if (message?.type === 'ruling') {
            const incoming = toRuling(message.data);
            if (incoming) setRulings((current) => mergeByKey(current, [incoming], (r) => r.caseKey));
          }
        } catch {
          /* ignore malformed broadcasts */
        }
      };
      socket.onclose = () => {
        socketRef.current = null;
        if (!closed) retry = window.setTimeout(connect, 5000 + Math.random() * 5000);
      };
      socket.onerror = () => socket.close();
    };

    connect();
    return () => {
      closed = true;
      if (retry !== null) window.clearTimeout(retry);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [tables]);

  /* ── Derived ── */
  const rosterNames = [...feed.roster].map((p) => p.name).sort((a, b) => a.localeCompare(b));
  const standings = computeStandings(rulings, feed.roster);
  const hasHistory = (s: (typeof standings)[number]) =>
    s.fineCount > 0 || s.bonusCount > 0 || s.dismissedCount > 0;
  const wanted = standings.filter(hasHistory);
  const clean = standings.filter((s) => !hasHistory(s));
  const fund = mojitoFund(rulings);
  const docket = pendingDocket(cases, rulings);
  const record = kourtRecord(cases, rulings);

  /* ── Filing ── */
  const applyGuideline = (g: (typeof SENTENCING_GUIDELINES)[number]) => {
    setKind(g.kind);
    setAmount(g.amount);
    setOffense(g.offense);
  };

  const clearPhotoDraft = useCallback(() => {
    setPhotoDraft((current) => {
      if (current) URL.revokeObjectURL(current.preview);
      return null;
    });
  }, []);

  const pickPhoto = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setPhotoBusy(true);
    const blob = await compressPhoto(file);
    setPhotoBusy(false);
    if (blob) {
      clearPhotoDraft();
      setPhotoDraft({ blob, preview: URL.createObjectURL(blob) });
    } else {
      setNotice('Could not read that photo — try a JPEG or PNG.');
    }
  };

  const fileComplaint = async (event: FormEvent) => {
    event.preventDefault();
    if (!tables || !user || filing) return;
    const text = offense.trim().slice(0, 300);
    if (!defendant || !text) return;
    const dollars = Math.min(COMPLAINT_MAX, Math.max(1, Math.round(amount)));
    setFiling(true);
    const rowKey = messageRowKey();
    let photoName: string | undefined;
    if (photoDraft && photos) {
      photoName = `${rowKey}.jpg`;
      const uploaded = await uploadPhoto(photos, photoName, photoDraft.blob);
      if (!uploaded) {
        setNotice('Could not upload the photo — the complaint was not filed. Try again.');
        setFiling(false);
        return;
      }
    }
    const filed: KourtCase = {
      rowKey,
      defendant,
      kind,
      amount: dollars,
      offense: text,
      filedBy: user.nick,
      filedAt: new Date().toISOString(),
      ...(photoName ? { photoName } : {}),
    };
    const ok = await insertCase(tables, filed);
    if (ok) {
      setCases((current) => mergeByKey(current, [filed], (c) => c.rowKey));
      setOffense('');
      clearPhotoDraft();
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({ type: 'case', data: filed }));
      }
      setNotice('Complaint filed. The Bench will rule in its own time.');
    } else {
      setNotice('The clerk dropped the paperwork — the complaint was not filed. Try again.');
    }
    setFiling(false);
  };

  /* ── Chambers ── */
  const explainRefusal = (status: number, reason: string): string => {
    if (status === 401) return 'That is not the gavel.';
    if (status === 429) return 'Too many tries. The gavel drawer is locked for ten minutes.';
    if (status === 503) return 'No gavel has been issued yet — the judge’s passcode is not set on the server.';
    if (status === 0) return 'The Kourt is unreachable right now.';
    return `The Kourt refused (${reason}).`;
  };

  const recess = () => {
    try {
      sessionStorage.removeItem(JUDGE_KEY);
    } catch {
      /* nothing to clear */
    }
    setJudge(null);
  };

  const unlockChambers = async (event: FormEvent) => {
    event.preventDefault();
    const code = passcode.trim();
    if (!code || unlocking) return;
    setUnlocking(true);
    const result = await verifyJudge(code);
    if (result.ok) {
      try {
        sessionStorage.setItem(JUDGE_KEY, code);
      } catch {
        /* still unlocked for this render */
      }
      setJudge(code);
      setPasscode('');
      setNotice('Chambers unlocked. Kourt is in session.');
    } else {
      setNotice(explainRefusal(result.status, result.reason));
    }
    setUnlocking(false);
  };

  /** "So ordered" plus whatever else the server managed to do about it. */
  const orderedNotice = (
    prefix: string,
    r: { ruling: KourtRuling; emailed: boolean; texted: boolean },
  ): string => {
    const bits = [prefix];
    if (r.ruling.summonsName) bits.push('Summons issued.');
    if (r.emailed) bits.push('The team has been emailed.');
    if (r.texted) bits.push('The defendant has been served by text.');
    return bits.join(' ');
  };

  const draftFor = (c: KourtCase): RulingDraft =>
    drafts[c.rowKey] ?? { verdict: c.kind === 'fine' ? 'fined' : 'bonus', amount: c.amount, opinion: '' };

  const updateDraft = (c: KourtCase, patch: Partial<RulingDraft>) =>
    setDrafts((current) => ({ ...current, [c.rowKey]: { ...draftFor(c), ...patch } }));

  /** Shared failure handling for both ways of ruling. */
  const rulingRefused = (status: number, reason: string) => {
    if (status === 409) {
      setNotice('The Kourt has already spoken on that one.');
      if (tables) void refresh(tables);
    } else if (status === 401) {
      recess();
      setNotice('The gavel was refused — enter chambers again.');
    } else if (status === 404) {
      setNotice('That complaint is no longer on the docket.');
      if (tables) void refresh(tables);
    } else {
      setNotice(explainRefusal(status, reason));
    }
  };

  const bangGavel = async (c: KourtCase) => {
    if (!judge || rulingOn) return;
    const d = draftFor(c);
    const opinion = d.opinion.trim();
    if (!opinion) {
      setNotice('An opinion is required. The Kourt must explain itself, however briefly.');
      return;
    }
    setRulingOn(c.rowKey);
    const result = await ruleOnCase(judge, {
      caseKey: c.rowKey,
      verdict: d.verdict,
      amount: d.verdict === 'dismissed' ? 0 : Math.max(0, Math.round(d.amount)),
      opinion,
    });
    if (result.ok) {
      setRulings((current) => mergeByKey(current, [result.ruling], (r) => r.caseKey));
      setDrafts((current) => {
        const next = { ...current };
        delete next[c.rowKey];
        return next;
      });
      setNotice(orderedNotice('So ordered.', result));
    } else {
      rulingRefused(result.status, result.reason);
    }
    setRulingOn(null);
  };

  const fileSuaSponte = async (event: FormEvent) => {
    event.preventDefault();
    if (!judge || suaSponteBusy) return;
    const s = suaSponte;
    if (!s.defendant || !s.offense.trim() || !s.opinion.trim()) {
      setNotice('The Bench must name a defendant, a charge and an opinion.');
      return;
    }
    setSuaSponteBusy(true);
    const result = await ruleSuaSponte(judge, {
      defendant: s.defendant,
      verdict: s.verdict,
      amount: Math.max(0, Math.round(s.amount)),
      offense: s.offense.trim(),
      opinion: s.opinion.trim(),
    });
    if (result.ok) {
      setCases((current) => mergeByKey(current, [result.case], (c) => c.rowKey));
      setRulings((current) => mergeByKey(current, [result.ruling], (r) => r.caseKey));
      setSuaSponte(EMPTY_SUA_SPONTE);
      setNotice(orderedNotice('So ordered, sua sponte.', result));
    } else {
      rulingRefused(result.status, result.reason);
    }
    setSuaSponteBusy(false);
  };

  const saveOptIn = async (event: FormEvent) => {
    event.preventDefault();
    if (!tables || !user || optInBusy) return;
    const phone = normalizePhone(optInPhone);
    if (!optInName) {
      setNotice('Tell the Kourt which roster name is yours.');
      return;
    }
    if (!phone) {
      setNotice('That does not look like a US mobile number. Ten digits, please.');
      return;
    }
    setOptInBusy(true);
    const filed: PhoneOptIn = { rowKey: messageRowKey(), email: user.email, rosterName: optInName, phone };
    const ok = await insertPhoneOptIn(tables, filed);
    if (ok) {
      setOptIn(filed);
      setOptInPhone('');
      setNotice(`Noted. When the Bench fines ${optInName}, ${formatPhone(phone)} hears about it first.`);
    } else {
      setNotice('The clerk could not record that number. Try again.');
    }
    setOptInBusy(false);
  };

  const notConfigured = ready && !tables;

  return (
    <div className="kourt-page page-content">
      {/* ── The Bench ── */}
      <div className="kourt-bench">
        <div className="kourt-bench-grid">
          <div className="kourt-bench-copy">
            <div className="kourt-bench-head">
              <span className="kourt-gavel" aria-hidden="true">
                ⚖️
              </span>
              <div>
                <span className="kourt-kicker">Towne Lake Hills Tennis &middot; Fines &amp; Bonuses</span>
                <h1 className="kourt-title">Kangaroo Kourt</h1>
                <p className="kourt-presiding">
                  {JUDGE_NAME}, presiding
                  <span className="kourt-paw">🐾 Go Tigers</span>
                </p>
              </div>
            </div>
            <p className="kourt-lede">
              Every fine goes into the <strong>mojito fund</strong>. Every bonus comes out of what
              you owe it. Anyone can file a complaint against a teammate; the Bench reads it, rules
              on it, and writes it up. You will not enjoy the write-up.
            </p>
          </div>
          <figure className="kourt-judge">
            <img
              src="/images/judge-tisdale.jpg"
              alt="William Tisdale on a tennis court in a judge's robe and a Clemson Tigers cap, holding an oversized gavel"
            />
            <figcaption>
              The Less Than Honorable William Tisdale
              <span>Kourt is in session. Go Tigers.</span>
            </figcaption>
          </figure>
        </div>
        <ol className="kourt-rules">
          {RULES.map((rule, i) => (
            <li key={rule}>
              <span className="kourt-rule-num">Rule {i + 1}</span>
              {rule}
            </li>
          ))}
        </ol>
      </div>

      {notice && (
        <p className="kourt-notice" role="status">
          {notice}
        </p>
      )}

      {/* ── Mojito Fund ── */}
      <div className="kourt-fund">
        <span className="kourt-fund-icon" aria-hidden="true">
          🍹
        </span>
        <div className="kourt-fund-body">
          <span className="kourt-fund-label">The Mojito Fund</span>
          <span className="kourt-fund-total">{formatDollars(fund.net)}</span>
          <span className="kourt-fund-detail">
            {formatDollars(fund.gross)} in fines &middot; {formatDollars(fund.credits)} in credits
            &middot; {plural(docket.length, 'complaint')} awaiting the Bench
          </span>
        </div>
      </div>

      {/* ── Most Wanted ── */}
      <section className="kourt-section">
        <div className="kourt-heading">
          <h2>Most Wanted</h2>
          <p>
            Ranked by what they owe the fund. Tap a name for the rap sheet. Positions are updated
            the moment the gavel comes down, and not one second before.
          </p>
        </div>
        {!ready ? (
          <p className="kourt-empty">Calling the docket&hellip;</p>
        ) : wanted.length === 0 ? (
          <p className="kourt-empty">
            Nobody has been fined yet. The Bench finds this suspicious and will be looking into it.
          </p>
        ) : (
          <ol className="kourt-board">
            {wanted.map((s) => (
              <li
                key={s.name}
                className={`kourt-row${s.owed > 0 ? ' kourt-row-owes' : s.owed < 0 ? ' kourt-row-credit' : ''}`}
              >
                <button
                  type="button"
                  className="kourt-row-main"
                  aria-expanded={expanded === s.name}
                  onClick={() => setExpanded(expanded === s.name ? null : s.name)}
                >
                  <span className="kourt-rank">#{String(s.rank).padStart(2, '0')}</span>
                  <span className="kourt-row-body">
                    <span className="kourt-name">
                      {s.name}
                      {!s.onRoster && <span className="kourt-flag">not on roster</span>}
                    </span>
                    <span className="kourt-title-chip">{s.title}</span>
                  </span>
                  <span className="kourt-owed">
                    {formatDollars(s.owed)}
                    <small>
                      {plural(s.fineCount, 'fine')} &middot; {plural(s.bonusCount, 'bonus', 'bonuses')}
                      {s.dismissedCount > 0 && <> &middot; {s.dismissedCount} dismissed</>}
                    </small>
                  </span>
                </button>
                {expanded === s.name && (
                  <ul className="kourt-sheet">
                    {rapSheet(s.name, cases, rulings).map((entry) => (
                      <RecordItem key={entry.ruling.caseKey} entry={entry} showDefendant={false} photos={photos} />
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ol>
        )}
        {ready && clean.length > 0 && (
          <div className="kourt-clean">
            <span className="kourt-clean-label">Clean record (for now):</span>
            {clean.map((s) => (
              <span key={s.name} className="kourt-chip">
                {s.name}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* ── The Docket ── */}
      <section className="kourt-section">
        <div className="kourt-heading">
          <h2>The Docket</h2>
          <p>Complaints awaiting the Bench. A recommendation is just that — the Bench sets the amount.</p>
        </div>
        {!ready ? (
          <p className="kourt-empty">Calling the docket&hellip;</p>
        ) : docket.length === 0 ? (
          <p className="kourt-empty">The docket is clear. Somebody file something.</p>
        ) : (
          <ul className="kourt-docket">
            {docket.map((c) => {
              const d = draftFor(c);
              const busy = rulingOn === c.rowKey;
              return (
                <li key={c.rowKey} className="kourt-case">
                  <div className="kourt-case-head">
                    <span className={`kourt-kind kourt-kind-${c.kind}`}>
                      {c.kind === 'fine' ? 'Fine' : 'Bonus'} &middot; {formatDollars(c.amount)} recommended
                    </span>
                    <span className="kourt-when">
                      Filed by {c.filedBy} &middot; {whenLabel(c.filedAt)}
                    </span>
                  </div>
                  <p className="kourt-case-body">
                    <strong>{c.defendant}</strong> &mdash; {c.offense}
                  </p>
                  <CasePhoto photos={photos} filed={c} />
                  {judge && (
                    <form
                      className="kourt-ruling-form"
                      onSubmit={(e) => {
                        e.preventDefault();
                        void bangGavel(c);
                      }}
                    >
                      <div className="kourt-verdicts" role="radiogroup" aria-label="Verdict">
                        {(['fined', 'bonus', 'dismissed'] as Verdict[]).map((v) => (
                          <label key={v} className={`kourt-verdict${d.verdict === v ? ' kourt-verdict-on' : ''}`}>
                            <input
                              type="radio"
                              name={`verdict-${c.rowKey}`}
                              value={v}
                              checked={d.verdict === v}
                              onChange={() => updateDraft(c, { verdict: v })}
                            />
                            {VERDICT_STAMP[v]}
                          </label>
                        ))}
                        <label className="kourt-amount">
                          $
                          <input
                            type="number"
                            min={0}
                            max={500}
                            inputMode="numeric"
                            value={d.verdict === 'dismissed' ? 0 : d.amount}
                            disabled={d.verdict === 'dismissed'}
                            onChange={(e) => updateDraft(c, { amount: Number(e.target.value) })}
                            aria-label="Amount in dollars"
                          />
                        </label>
                      </div>
                      <div className="kourt-openers">
                        {OPINION_OPENERS.map((line) => (
                          <button
                            key={line}
                            type="button"
                            className="kourt-chip kourt-chip-btn"
                            onClick={() =>
                              updateDraft(c, { opinion: d.opinion ? `${d.opinion.trimEnd()} ${line}` : line })
                            }
                          >
                            {line.length > 42 ? `${line.slice(0, 40)}…` : line}
                          </button>
                        ))}
                      </div>
                      <textarea
                        rows={3}
                        maxLength={1000}
                        placeholder="Opinion of the Kourt…"
                        aria-label="Opinion of the Kourt"
                        value={d.opinion}
                        onChange={(e) => updateDraft(c, { opinion: e.target.value })}
                      />
                      <div className="kourt-form-actions">
                        <button type="submit" className="kourt-btn kourt-btn-gavel" disabled={busy}>
                          {busy ? 'Ruling…' : '🔨 Bang the Gavel'}
                        </button>
                      </div>
                    </form>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ── Kourt Record ── */}
      <section className="kourt-section">
        <div className="kourt-heading">
          <h2>Kourt Record</h2>
          <p>Every ruling ever entered, newest first, with the opinion of the Kourt. Nothing here can be edited. Ever.</p>
        </div>
        {!ready ? (
          <p className="kourt-empty">Opening the record&hellip;</p>
        ) : record.length === 0 ? (
          <p className="kourt-empty">The record is empty. This will not last.</p>
        ) : (
          <ul className="kourt-record-list">
            {record.map((entry) => (
              <RecordItem key={entry.ruling.caseKey} entry={entry} showDefendant photos={photos} />
            ))}
          </ul>
        )}
      </section>

      {/* ── File a Complaint ── */}
      <section className="kourt-section" id="file">
        <div className="kourt-heading">
          <h2>File a Complaint</h2>
          <p>
            See something, say something. Pick a teammate, pick fine or bonus, suggest a dollar
            figure (up to {formatDollars(COMPLAINT_MAX)} &mdash; only the Bench goes higher) and
            state the offense. Your name goes on the filing.
          </p>
        </div>
        {notConfigured ? (
          <p className="kourt-hint">The Kourt&apos;s records are not configured yet.</p>
        ) : !ready ? null : user ? (
          <form className="kourt-form" onSubmit={fileComplaint}>
            <div className="kourt-guidelines" aria-label="Sentencing guidelines">
              {SENTENCING_GUIDELINES.map((g) => (
                <button
                  key={g.label}
                  type="button"
                  className={`kourt-chip kourt-chip-btn kourt-chip-${g.kind}`}
                  onClick={() => applyGuideline(g)}
                  title={g.offense}
                >
                  {g.kind === 'fine' ? '−' : '+'}
                  {g.amount} {g.label}
                </button>
              ))}
            </div>
            <div className="kourt-form-grid">
              <label className="kourt-field">
                Defendant
                <select value={defendant} onChange={(e) => setDefendant(e.target.value)} required>
                  <option value="">Pick a teammate…</option>
                  {rosterNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
              <fieldset className="kourt-field kourt-kind-toggle">
                <legend>Recommending</legend>
                <label className={kind === 'fine' ? 'kourt-verdict kourt-verdict-on' : 'kourt-verdict'}>
                  <input type="radio" name="kind" checked={kind === 'fine'} onChange={() => setKind('fine')} />
                  A fine
                </label>
                <label className={kind === 'bonus' ? 'kourt-verdict kourt-verdict-on' : 'kourt-verdict'}>
                  <input type="radio" name="kind" checked={kind === 'bonus'} onChange={() => setKind('bonus')} />
                  A bonus
                </label>
              </fieldset>
              <label className="kourt-field kourt-field-amount">
                Amount ($)
                <input
                  type="number"
                  min={1}
                  max={COMPLAINT_MAX}
                  inputMode="numeric"
                  value={amount}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  required
                />
              </label>
            </div>
            <label className="kourt-field">
              The offense (or the good deed)
              <textarea
                rows={3}
                maxLength={300}
                placeholder="What did he do?"
                value={offense}
                onChange={(e) => setOffense(e.target.value)}
                required
              />
            </label>
            {photoDraft && (
              <div className="kourt-photo-preview">
                <img src={photoDraft.preview} alt="Exhibit A preview" />
                <button
                  type="button"
                  className="kourt-photo-remove"
                  aria-label="Remove photo"
                  onClick={clearPhotoDraft}
                >
                  ✕
                </button>
              </div>
            )}
            <div className="kourt-form-actions">
              <span className="kourt-filing-as">Filing as {user.nick}</span>
              {photos && (
                <span className="kourt-photo-buttons">
                  {canCapture && (
                    <label className={`kourt-photo-attach${photoBusy ? ' kourt-photo-attach-busy' : ''}`}>
                      📷 Take Photo
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        hidden
                        disabled={photoBusy || filing}
                        onChange={pickPhoto}
                      />
                    </label>
                  )}
                  <label className={`kourt-photo-attach${photoBusy ? ' kourt-photo-attach-busy' : ''}`}>
                    {photoBusy ? 'Preparing…' : photoDraft ? '🖼️ Swap Photo' : '🖼️ Attach Photo'}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      disabled={photoBusy || filing}
                      onChange={pickPhoto}
                    />
                  </label>
                </span>
              )}
              <button
                type="submit"
                className="kourt-btn"
                disabled={filing || photoBusy || !defendant || !offense.trim()}
              >
                {filing ? 'Filing…' : 'File Complaint'}
              </button>
            </div>
          </form>
        ) : (
          <p className="kourt-hint">
            Complaints need a name on them. <Link to="/team">Register on the Team page</Link>{' '}
            (email and nickname, thirty seconds) and come back.
          </p>
        )}
      </section>

      {/* ── Serve me by text ── */}
      <section className="kourt-section" id="serve">
        <div className="kourt-heading">
          <h2>Get Served by Text</h2>
          <p>
            When the Bench fines you, the Kourt can text you the bad news and a link to your
            summons. Tell it which roster name is yours and where to send it. Only you are texted,
            and only when it is you being fined.
          </p>
        </div>
        {notConfigured ? (
          <p className="kourt-hint">The Kourt&apos;s records are not configured yet.</p>
        ) : !ready ? null : user ? (
          <form className="kourt-form" onSubmit={saveOptIn}>
            {optIn && (
              <p className="kourt-optin-current">
                📱 Texts go to <strong>{formatPhone(optIn.phone)}</strong> as{' '}
                <strong>{optIn.rosterName}</strong>. Enter a new number below to change it.
              </p>
            )}
            <div className="kourt-form-grid kourt-form-grid-two">
              <label className="kourt-field">
                Your roster name
                <select value={optInName} onChange={(e) => setOptInName(e.target.value)} required>
                  <option value="">Pick yourself…</option>
                  {rosterNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="kourt-field">
                Mobile number
                <input
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder="(770) 555-1212"
                  value={optInPhone}
                  onChange={(e) => setOptInPhone(e.target.value)}
                  required
                />
              </label>
            </div>
            <div className="kourt-form-actions">
              <span className="kourt-filing-as">Filed under {user.email}</span>
              <button
                type="submit"
                className="kourt-btn"
                disabled={optInBusy || !optInName || !optInPhone.trim()}
              >
                {optInBusy ? 'Saving…' : optIn ? 'Update Number' : 'Serve Me by Text'}
              </button>
            </div>
          </form>
        ) : (
          <p className="kourt-hint">
            <Link to="/team">Register on the Team page</Link> first, then come back and tell the
            Kourt where to send the bad news.
          </p>
        )}
      </section>

      {/* ── Judge's Chambers ── */}
      <section className="kourt-section kourt-chambers">
        <div className="kourt-heading">
          <h2>Judge&apos;s Chambers</h2>
          <p>
            {judge
              ? 'Kourt is in session. Rule on the docket above, or fine someone on your own motion.'
              : 'Members of the Bench only. Everyone else may admire the door.'}
          </p>
        </div>
        {judge ? (
          <>
            <form className="kourt-form" onSubmit={fileSuaSponte}>
              <h3 className="kourt-subhead">Sua sponte</h3>
              <div className="kourt-form-grid">
                <label className="kourt-field">
                  Defendant
                  <select
                    value={suaSponte.defendant}
                    onChange={(e) => setSuaSponte({ ...suaSponte, defendant: e.target.value })}
                    required
                  >
                    <option value="">Pick a teammate…</option>
                    {rosterNames.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </label>
                <fieldset className="kourt-field kourt-kind-toggle">
                  <legend>Order</legend>
                  <label className={suaSponte.verdict === 'fined' ? 'kourt-verdict kourt-verdict-on' : 'kourt-verdict'}>
                    <input
                      type="radio"
                      name="ss-verdict"
                      checked={suaSponte.verdict === 'fined'}
                      onChange={() => setSuaSponte({ ...suaSponte, verdict: 'fined' })}
                    />
                    Fined
                  </label>
                  <label className={suaSponte.verdict === 'bonus' ? 'kourt-verdict kourt-verdict-on' : 'kourt-verdict'}>
                    <input
                      type="radio"
                      name="ss-verdict"
                      checked={suaSponte.verdict === 'bonus'}
                      onChange={() => setSuaSponte({ ...suaSponte, verdict: 'bonus' })}
                    />
                    Credited
                  </label>
                </fieldset>
                <label className="kourt-field kourt-field-amount">
                  Amount ($)
                  <input
                    type="number"
                    min={0}
                    max={500}
                    inputMode="numeric"
                    value={suaSponte.amount}
                    onChange={(e) => setSuaSponte({ ...suaSponte, amount: Number(e.target.value) })}
                    required
                  />
                </label>
              </div>
              <label className="kourt-field">
                Charge
                <input
                  type="text"
                  maxLength={300}
                  placeholder="What the Bench observed"
                  value={suaSponte.offense}
                  onChange={(e) => setSuaSponte({ ...suaSponte, offense: e.target.value })}
                  required
                />
              </label>
              <label className="kourt-field">
                Opinion of the Kourt
                <textarea
                  rows={3}
                  maxLength={1000}
                  value={suaSponte.opinion}
                  onChange={(e) => setSuaSponte({ ...suaSponte, opinion: e.target.value })}
                  required
                />
              </label>
              <div className="kourt-form-actions">
                <button type="button" className="kourt-btn kourt-btn-quiet" onClick={recess}>
                  Recess
                </button>
                <button type="submit" className="kourt-btn kourt-btn-gavel" disabled={suaSponteBusy}>
                  {suaSponteBusy ? 'Ruling…' : '🔨 So Ordered'}
                </button>
              </div>
            </form>
          </>
        ) : configured ? (
          <form className="kourt-unlock" onSubmit={unlockChambers}>
            <label className="kourt-field">
              Passcode
              <input
                type="password"
                autoComplete="off"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                required
              />
            </label>
            <button type="submit" className="kourt-btn" disabled={unlocking || !passcode.trim()}>
              {unlocking ? 'Checking…' : 'Enter Chambers'}
            </button>
          </form>
        ) : (
          <p className="kourt-hint">
            {ready
              ? 'No gavel has been issued yet — the judge’s passcode is not set on the server.'
              : 'Checking whether a gavel has been issued…'}
          </p>
        )}
      </section>
    </div>
  );
}
